import { customerRulesPath } from "@/lib/businessRules";
import { Link } from "react-router-dom";
import { CreditCard, LifeBuoy, Video, XCircle } from "lucide-react";
import Seo from "@/components/Seo";

const topics = [
  {
    icon: CreditCard,
    title: "Betaling",
    questions: [
      {
        question: "Hvordan foregår betalingen?",
        answer: "Når du har valgt et prisforslag aftaler du betalingen direkte med eksperten. Eksperten bekræfter modtagelsen på SpørgMig før videosamtalen kan åbnes.",
      },
      {
        question: "Hvornår bliver prisen bindende?",
        answer: "Du modtager enten en fast pris eller et timebaseret tilbud. Aftalen bliver først bindende når du selv accepterer pris, omfang og tidspunkt.",
      },
    ],
  },
  {
    icon: XCircle,
    title: "Aflysning",
    questions: [
      {
        question: "Hvad sker der hvis kunden aflyser?",
        answer: "Ved aflysning mindst 3 timer før det aftalte tidspunkt har kunden ret til hele beløbet tilbage. Ved mindre end 3 timer fordeles beløbet med 50 procent til kunden og 50 procent til eksperten.",
      },
      {
        question: "Hvad sker der hvis eksperten aflyser?",
        answer: "Hvis eksperten aflyser har kunden ret til hele beløbet tilbage. Da betalingen sker direkte mellem parterne gennemføres tilbagebetalingen også direkte mellem kunden og eksperten.",
      },
    ],
  },
  {
    icon: Video,
    title: "Videosamtale",
    questions: [
      {
        question: "Hvordan deltager jeg i samtalen?",
        answer: "Åbn den valgte forespørgsel eller konsultation fra dit dashboard. Når betalingen er bekræftet kan du åbne videosamtalen fra aftalen.",
      },
      {
        question: "Hvad bør jeg gøre før samtalen?",
        answer: "Sørg for en stabil internetforbindelse og giv browseren adgang til kamera og mikrofon. Hav relevante billeder, dokumenter eller oplysninger klar.",
      },
    ],
  },
  {
    icon: LifeBuoy,
    title: "Support",
    questions: [
      {
        question: "Hvordan får jeg hjælp til en aftale?",
        answer: "Åbn den konkrete forespørgsel og vælg support. Så bliver sagen knyttet til den rigtige aftale og de relevante oplysninger.",
      },
      {
        question: "Hvordan kontakter jeg SpørgMig?",
        answer: "Du kan bruge kontaktsiden hvis dit spørgsmål ikke handler om en bestemt aftale. Beskriv problemet så konkret som muligt.",
      },
    ],
  },
];

export default function HelpPage() {
  return (
    <>
      <Seo
        title="Hjælp – betaling, aflysning og videosamtaler | SpørgMig"
        description="Få svar om betaling, aflysning, videosamtaler og support på SpørgMig."
        path="/hjaelp"
      />
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
        <header className="max-w-2xl">
          <div className="font-mono text-[11px] uppercase tracking-widest text-brand-fresh">Hjælp</div>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-[-0.02em] text-brand-ink sm:text-4xl">
            Hvad kan vi hjælpe med?
          </h1>
          <p className="mt-3 text-sm leading-6 text-brand-body sm:text-base">
            Her finder du korte svar om hele forløbet fra prisforslag til afsluttet samtale.
          </p>
        </header>

        <p className="mt-4 text-sm"><Link to={customerRulesPath} className="text-brand-green underline underline-offset-4">Betaling, afbud og regler</Link></p>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {topics.map(({ icon: Icon, title, questions }) => (
            <section key={title} className="rounded-2xl border border-brand-line bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2 text-brand-green">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon size={18} aria-hidden="true"/>
                </span>
                <h2 className="font-display text-lg font-semibold text-brand-ink">{title}</h2>
              </div>
              <div className="mt-4 divide-y divide-brand-line">
                {questions.map(({ question, answer }) => (
                  <details key={question} className="group py-3">
                    <summary className="cursor-pointer list-none pr-6 text-sm font-medium text-brand-ink marker:content-none">
                      {question}
                    </summary>
                    <p className="mt-2 text-sm leading-6 text-brand-body">{answer}</p>
                  </details>
                ))}
              </div>
            </section>
          ))}
        </div>

        <section className="mt-6 rounded-2xl bg-brand-green p-5 text-white sm:flex sm:items-center sm:justify-between sm:gap-6 sm:p-6">
          <div>
            <h2 className="font-display text-xl font-semibold">Fandt du ikke svaret?</h2>
            <p className="mt-1 text-sm text-white/75">Kontakt os så hjælper vi dig videre.</p>
          </div>
          <Link to="/kontakt" className="mt-4 inline-flex min-h-11 items-center justify-center rounded-xl bg-white px-5 text-sm font-semibold text-brand-green sm:mt-0">
            Kontakt SpørgMig
          </Link>
        </section>
      </div>
    </>
  );
}

